import React, { useEffect, useMemo, useState } from 'react';
import { endOfMonth, endOfWeek, endOfYear, format, startOfMonth, startOfToday, startOfWeek, startOfYear, subDays, subMonths, subWeeks } from 'date-fns';
import { ru } from 'date-fns/locale';
import { DateRange, DefinedRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Separator } from './ui/separator';
import { cn } from '../lib/utils';

const DateRangePicker = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectionRange, setSelectionRange] = useState(() => ({
    startDate: value?.from ?? startOfToday(),
    endDate: value?.to ?? startOfToday(),
    key: 'selection'
  }));

  useEffect(() => {
    if (value?.from && value?.to) {
      setSelectionRange({ startDate: value.from, endDate: value.to, key: 'selection' });
    }
  }, [value]);

  const presetRanges = useMemo(
    () => [
      {
        label: 'Сегодня',
        range: () => {
          const today = startOfToday();
          return { startDate: today, endDate: today };
        }
      },
      {
        label: 'Вчера',
        range: () => {
          const yesterday = subDays(startOfToday(), 1);
          return { startDate: yesterday, endDate: yesterday };
        }
      },
      {
        label: 'Текущая неделя',
        range: () => {
          const start = startOfWeek(new Date(), { weekStartsOn: 1 });
          return { startDate: start, endDate: endOfWeek(start, { weekStartsOn: 1 }) };
        }
      },
      {
        label: 'Предыдущая неделя',
        range: () => {
          const start = startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 });
          return { startDate: start, endDate: endOfWeek(start, { weekStartsOn: 1 }) };
        }
      },
      {
        label: 'Текущий месяц',
        range: () => {
          const start = startOfMonth(new Date());
          return { startDate: start, endDate: endOfMonth(start) };
        }
      },
      {
        label: 'Предыдущий месяц',
        range: () => {
          const start = startOfMonth(subMonths(new Date(), 1));
          return { startDate: start, endDate: endOfMonth(start) };
        }
      },
      {
        label: 'Текущий год',
        range: () => {
          const start = startOfYear(new Date());
          return { startDate: start, endDate: endOfYear(start) };
        }
      }
    ],
    []
  );

  const getDateRangeText = () => {
    const from = selectionRange?.startDate;
    const to = selectionRange?.endDate;
    if (!from || !to) return 'Выберите период';
    if (format(from, 'yyyy-MM-dd') === format(to, 'yyyy-MM-dd')) {
      return format(from, 'd MMM yyyy', { locale: ru });
    }
    return `${format(from, 'd MMM', { locale: ru })} - ${format(to, 'd MMM yyyy', { locale: ru })}`;
  };

  const applyRange = (range) => {
    const normalized = { ...range, key: 'selection' };
    setSelectionRange(normalized);
    onChange?.({ from: normalized.startDate, to: normalized.endDate });
    setIsOpen(false);
  };

  const handleRangeChange = (ranges) => {
    const next = ranges.selection;
    setSelectionRange(next);
    if (next?.startDate && next?.endDate) {
      onChange?.({ from: next.startDate, to: next.endDate });
    }
  };

  const handleClear = () => {
    setSelectionRange(undefined);
    onChange?.(undefined);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-[320px] justify-start text-left font-normal',
            !selectionRange?.startDate && 'text-muted-foreground',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {getDateRangeText()}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[360px] sm:w-[900px] max-w-[calc(100vw-24px)] max-h-[80vh] overflow-auto"
        align="center"
        sideOffset={10}
        collisionPadding={16}
      >
        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-56 border-b sm:border-b-0 sm:border-r bg-muted/40 p-3 space-y-2">
            {presetRanges.map((preset) => (
              <Button
                key={preset.label}
                variant="ghost"
                size="sm"
                className="w-full justify-start"
                onClick={() => applyRange(preset.range())}
              >
                {preset.label}
              </Button>
            ))}
          </div>
          <div className="flex-1 p-3">
            <div className="overflow-x-auto pb-1">
              <DateRange
                locale={ru}
                ranges={[selectionRange ?? { startDate: startOfToday(), endDate: startOfToday(), key: 'selection' }]}
                onChange={handleRangeChange}
                months={2}
                direction="horizontal"
                showDateDisplay={false}
                weekStartsOn={1}
              />
            </div>
            <Separator className="my-3" />
            <div className="flex items-center justify-between space-x-2">
              <Button variant="ghost" size="sm" onClick={handleClear}>
                Очистить
              </Button>
              <Button size="sm" onClick={() => setIsOpen(false)} disabled={!selectionRange?.startDate || !selectionRange?.endDate}>
                Готово
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
